// =============================================================================
// courseMarkdown — display-only cleanup for course md in the side panels
// =============================================================================
// The Google Docs sync writes one line per doc paragraph with NO blank line
// between them. Markdown joins adjacent lines into one paragraph, so the panels
// rendered walls of text: a list line swallowed the paragraph after it, and a
// table right after a list line printed as raw pipes ("| Section | ... |---|").
//
// spaceCourseBlocks() runs AFTER a panel's formatScript()/scriptOnly(), so the
// script classifier still sees the raw lines and the transcript highlight and the
// script bar keep agreeing. Same rules as normalizeLesson() in pages/CoursePage.tsx
// (the Course page), minus the page-only parts (title dedup, card blocks).
//   1. Blank lines go back between blocks. Table rows, list runs, blockquote runs
//      (the script bar), indented continuations and fenced code stay tight.
//   2. "──────" rule lines become a real <hr>.
//   3. "****" is an empty bold pair the doc left behind. It goes.
//   4. A bold run that ends on a space ("**Word **") never closes. The space
//      moves outside the marker.
//   5. A speaker label ("YOU:", "LEAD:", "CLOSER:") is bolded.
//   6. A Docs soft break syncs as a vertical tab (\v). It becomes a hard break.
//   7. "✅ A✅ B" / "☐ A ☐ B" fused onto one line become one item each.
//   8. The framework breadcrumb stays one line. 9. <video> tags become a link.
//  10. "- [ ] item" task items become "- ☐ item" (the panel li drops the bullet).
//  11. An indented outline under a plain line keeps its shape.
// The source md is never edited. Re-syncs overwrite it.
// =============================================================================

const isTableRow = (l: string) => /^\s*\|/.test(l);
const isListItem = (l: string) => /^\s*([-*+]|\d+[.)])\s+/.test(l);
const isQuote = (l: string) => /^\s*>/.test(l);
const isFence = (l: string) => /^\s*```/.test(l);
const isMarkLine = (l: string) => /^\s*(✅|❌|☐|☑)/.test(l);
const isIndented = (l: string) => /^\s{2,}\S/.test(l);
const NBSP = "\u00a0";

const fixEmphasisSpacing = (line: string): string => {
  const counts: Record<number, number> = {};
  return line.replace(/(\s*)(\*{2,3})(\s*)/g, (m, before: string, run: string, after: string, offset: number) => {
    const n = run.length;
    counts[n] = (counts[n] ?? 0) + 1;
    // A *** run also opens/closes a bold pair, so later ** runs pair against it.
    if (n === 3) counts[2] = (counts[2] ?? 0) + 1;
    const opening = counts[n] % 2 === 1;
    if (opening && after && offset + m.length < line.length) return `${before}${after}${run}`;
    if (!opening && before && offset > 0) return `${run}${before}${after}`;
    return m;
  });
};

const SPEAKER = /^(\s*(?:>\s*)?(?:[-*+]\s+)?)(YOU|LEAD|CLOSER|SETTER|PROSPECT|CLIENT|REP|THEM)\s*:\s*(?!\*)/;
// "You (FOLLOW-UP - dig in):" / "Lead (pause):" — a speaker with a delivery cue.
const CUED_SPEAKER = /^(\s*(?:>\s*)?)((?:YOU|LEAD|You|Lead|THEM|Them)\s*\([^)]{1,60}\)):\s*(?!\*)/;
// Inside a script bar, any CAPS name label ("> SARAH:", "> MIKE:") is a speaker.
const BAR_NAME = /^(\s*>\s*)([A-Z][A-Z'’]{1,20}):\s*(?!\*)/;

const cleanLine = (raw: string): string => {
  // Keep a trailing two-space hard break (the script bar stacks turns with it).
  const hardBreak = / {2}$/.test(raw);
  let line = raw;
  if (/^\s*[─━—-]{3,}\s*$/.test(line) && /[─━—]/.test(line)) return "---";
  // 8. The framework breadcrumb ("1. PRE-CALL  >  [ 3. DISCOVERY ]  >  …") is
  //    one line, not a list. It stays a paragraph, arrows between stages, and
  //    the bracketed current stage goes bold.
  if (/^\s*(?:\*{4})?1\.\s+PRE-CALL\s+>/.test(line))
    return line
      .trim()
      .replace(/^\*{4}/, "")
      .replace(/^1\./, "1\\.")
      .replace(/\[\s*([^\]]+?)\s*\]/g, "**$1**")
      .replace(/\s+>\s+/g, " → ");
  // 9. "<video>[url](url)</video>" prints its tags as text in a panel. It
  //    becomes one short link (the Course page embeds the player instead).
  line = line.replace(/<video>\s*\[[^\]]*\]\(([^)\s]+)\)\s*<\/video>/g, "[▶ Watch the training video]($1)");
  line = line.replace(/(\*{4,})([^\w\s*]{1,3})(\*{2,3})(?!\*)/g, (m, a: string, punct: string, b: string) =>
    a.length > b.length ? punct + "*".repeat(a.length - b.length) : m
  );
  // "***Bold lead.**** rest*": the "****" closes the bold only. Stripping it
  // whole left the bold open.
  line = line.replace(/(?<!\*)(\*\*\*)([^*]+?)\*{4}(?!\*)/g, "$1$2**");
  // "***A.***** B.**": "*****" is "***" closing + "**" opening.
  line = line.replace(/([^*\s])\s*\*{5}\s*(?=[^\s*])/g, "$1*** **");
  // "*Watch this: *[link]": an italic run closing on a space never closes.
  line = line.replace(/^(\s*)\*([^*\s][^*]*?)\s+\*(?=[^\s*])/, "$1*$2* ");
  // A link whose text is its own long URL runs off a narrow panel. It reads
  // as "Watch on Zoom" (or the host) instead.
  const label = (url: string) => {
    const host = /zoom\.us/.test(url) ? "Zoom" : /fathom\.video/.test(url) ? "Fathom" : /youtu/.test(url) ? "YouTube"
      : /loom\.com/.test(url) ? "Loom" : /drive\.google/.test(url) ? "Google Drive" : /docs\.google/.test(url) ? "Google Docs"
      : url.replace(/^https?:\/\/(www\.)?/, "").split("/")[0];
    return `[▶ Watch on ${host}](${url})`;
  };
  line = line.replace(/\[(https?:\/\/[^\]\s]+)\]\((https?:\/\/[^)\s]+)\)/g, (_m, _t: string, url: string) => label(url));
  // A bare URL (GFM autolinks it) does the same.
  line = line.replace(/(^|[\s:])(https?:\/\/[^\s)\]]+)(?=\s|$)/g, (_m, pre: string, url: string) => pre + label(url));
  line = line.replace(/\*{4,}/g, "");
  line = line.replace(SPEAKER, (_m, lead: string, who: string) => `${lead}**${who}:** `);
  line = line.replace(CUED_SPEAKER, (_m, lead: string, who: string) => `${lead}**${who}:** `);
  line = line.replace(BAR_NAME, (_m, lead: string, who: string) => `${lead}**${who}:** `);
  line = fixEmphasisSpacing(line).replace(/\s+$/, "");
  if (line.includes("\v")) {
    const quote = /^(\s*>\s*)/.exec(line)?.[1] ?? "";
    const marker = /^(\s*(?:[-*+]|\d+[.)])\s+)/.exec(line)?.[1];
    const indent = marker ? " ".repeat(marker.length) : "";
    const parts = line.slice(quote.length).split("\v").map((p) => p.trim()).filter(Boolean);
    line = quote + parts.join(`\\\n${quote}${indent}`);
  }
  // 10. A GFM task item ("- [ ] Went 4 to 5 layers deep") drew a bullet AND a
  //     checkbox. It becomes a "- ☐" item; the panel li drops the bullet for
  //     any marked item (hasMark) and hangs the wrapped lines.
  line = line.replace(/^(\s*[-*+]\s+)\[([ xX])\]\s+/, (_m, lead: string, x: string) => lead + (x === " " ? "☐ " : "☑ "));
  if (isMarkLine(line)) {
    line = line
      .trim()
      .split(/(?=✅|❌|☐|☑)/)
      .map((p) => p.trim())
      .filter((p) => p.length > 1)
      .map((p) => `- ${p}`)
      .join("\n");
  }
  // "> ❌ "Got a minute?" ✅ "Is now a terrible time?"" in a script bar: one
  // mark per line, stacked with hard breaks inside the bar.
  const q = /^(\s*>\s*)(.*)$/.exec(line);
  if (q && /(✅|❌).*(✅|❌)/.test(q[2]))
    line = q[2]
      .split(/(?=✅|❌)/)
      .map((p) => p.trim())
      .filter(Boolean)
      .map((p) => q[1] + p)
      .join("  \n");
  return hardBreak && line ? `${line}  ` : line;
};

export const spaceCourseBlocks = (md: string): string => {
  const out: string[] = [];
  let inFence = false;
  // Is the last un-indented line a list item? Then an indented line continues it.
  let listCtx = false;
  const lastLine = (s: string) => s.split("\n").pop() ?? "";
  const breakAfterPrev = () => {
    if (!/\\$/.test(out[out.length - 1])) out[out.length - 1] = out[out.length - 1].replace(/ +$/, "") + "\\";
  };
  for (const raw of md.split("\n")) {
    const fence = isFence(raw);
    if (fence) inFence = !inFence;
    let line = inFence || fence ? raw : cleanLine(raw);
    const prev = out.length ? lastLine(out[out.length - 1]) : "";
    // 11. An indented outline under a plain line ("Ask it" / "    > Layer 1")
    //     is a diagram, not a list or code. Markdown fused it into one line (or
    //     made a code block of 4+ spaces). It keeps its shape: hard breaks,
    //     no-break-space indent, "> " as an arrow.
    if (!inFence && !fence && isIndented(line) && !listCtx && prev.trim()) {
      const indent = /^\s*/.exec(line)![0].length;
      line = NBSP.repeat(indent) + line.trim().replace(/^>\s*/, "→ ");
      if (/^\s*(#|\|)/.test(prev)) out.push(""); // a heading/table can't take a break
      else breakAfterPrev();
      out.push(line);
      continue;
    }
    if (line.trim() && !isIndented(line)) listCtx = isListItem(line);
    const keepTight =
      inFence ||
      fence ||
      !prev.trim() ||
      !line.trim() ||
      (isTableRow(prev) && isTableRow(line)) ||
      (isListItem(prev) && isListItem(line)) ||
      (isQuote(prev) && isQuote(line)) ||
      (isIndented(line) && (isListItem(prev) || isIndented(prev)));
    if (!keepTight) out.push("");
    out.push(line);
  }
  return out.join("\n").replace(/\n{3,}/g, "\n\n");
};

// Does a react-markdown li (hast node) start with a checklist / do-don't mark?
// Then the mark is the marker and the bullet goes.
type HastNode = { type?: string; value?: string; children?: HastNode[] };
const firstText = (n: HastNode | undefined): string => {
  if (!n) return "";
  if (n.type === "text") return n.value ?? "";
  for (const c of n.children ?? []) {
    const t = firstText(c);
    if (t.trim()) return t;
  }
  return "";
};
export const hasMark = (node: unknown): boolean => /^\s*(☐|☑|✅|❌)/.test(firstText(node as HastNode));
